import React from 'react';
import { Project } from '../types/Project';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface ProjectListProps {
  projects: Project[];
  onSelectProject: (project: Project) => void;
  onDeleteProject: (id: string) => void;
}

export const ProjectList: React.FC<ProjectListProps> = ({ 
  projects, 
  onSelectProject, 
  onDeleteProject 
}) => {
  const getStatusColor = (status: Project['status']) => {
    switch (status) {
      case 'planning': return '#fbbf24';
      case 'in-progress': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'on-hold': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: Project['status']) => {
    switch (status) {
      case 'planning': return '計画中';
      case 'in-progress': return '進行中';
      case 'completed': return '完了';
      case 'on-hold': return '保留';
      default: return '不明';
    }
  };

  // ステータス別にプロジェクトをグループ化
  const groupedProjects = {
    planning: projects.filter(p => p.status === 'planning'),
    'in-progress': projects.filter(p => p.status === 'in-progress'),
    completed: projects.filter(p => p.status === 'completed'),
    'on-hold': projects.filter(p => p.status === 'on-hold')
  };

  const renderProjectCard = (project: Project) => (
    <div key={project.id} className="project-card" onClick={() => onSelectProject(project)}>
      <div className="project-header">
        <h3>{project.name}</h3>
        <button 
          className="delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            onDeleteProject(project.id);
          }}
        >
          ×
        </button>
      </div>
      
      <p className="project-description">{project.description}</p>
      
      <div className="project-meta">
        <div className="status-badge" style={{ backgroundColor: getStatusColor(project.status) }}>
          {getStatusText(project.status)}
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${project.progress}%` }}
          />
          <span className="progress-text">{project.progress}%</span>
        </div>
      </div>
      
      <div className="project-dates">
        <div>
          <strong>開始:</strong> {format(project.startDate, 'yyyy/MM/dd', { locale: ja })}
        </div>
        <div>
          <strong>終了:</strong> {format(project.endDate, 'yyyy/MM/dd', { locale: ja })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="project-list">
      <h2>案件一覧</h2>
      {projects.length === 0 ? (
        <div className="empty-state">
          <p>まだ案件がありません。新しい案件を作成してください。</p>
        </div>
      ) : (
        <div className="status-sections">
          {/* 進行中 */}
          {groupedProjects['in-progress'].length > 0 && (
            <div className="status-section">
              <h3 className="status-section-title in-progress">
                🔄 進行中 ({groupedProjects['in-progress'].length}件)
              </h3>
              <div className="project-grid">
                {groupedProjects['in-progress'].map(renderProjectCard)}
              </div>
            </div>
          )}

          {/* 計画中 */}
          {groupedProjects.planning.length > 0 && (
            <div className="status-section">
              <h3 className="status-section-title planning">
                📋 計画中 ({groupedProjects.planning.length}件)
              </h3>
              <div className="project-grid">
                {groupedProjects.planning.map(renderProjectCard)}
              </div>
            </div>
          )}

          {/* 保留 */}
          {groupedProjects['on-hold'].length > 0 && (
            <div className="status-section">
              <h3 className="status-section-title on-hold">
                ⏸️ 保留 ({groupedProjects['on-hold'].length}件)
              </h3>
              <div className="project-grid">
                {groupedProjects['on-hold'].map(renderProjectCard)}
              </div>
            </div>
          )}

          {/* 完了 */}
          {groupedProjects.completed.length > 0 && (
            <div className="status-section">
              <h3 className="status-section-title completed">
                ✅ 完了 ({groupedProjects.completed.length}件)
              </h3>
              <div className="project-grid">
                {groupedProjects.completed.map(renderProjectCard)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
